import React, { useState, useEffect } from "react";
import { CommonInput } from "../common";
import { DeliveryPartner } from "../../types/interfaces";
import useAxios from "../../hooks/useAxios";

type ModalProps = {
  show: boolean;
  handleModalOpenClose: (show: boolean) => void;
  order_id?: string;
  delivery_partner_id?: string;
  tracking_number?: string;
  getListData: () => void;
};

type OrderPartnerForm = {
	order_id: string;
	delivery_partner_id: string;
	tracking_number: string;
};

const UpdateDeliveryPartner: React.FC<ModalProps> = ({ show, handleModalOpenClose, order_id, delivery_partner_id, tracking_number, getListData }) => {
  const axiosInstance = useAxios();
  
  const [partners, setPartners] = useState<DeliveryPartner[] | null>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<OrderPartnerForm>({ order_id: '', delivery_partner_id: "", tracking_number: "" });
  const [errors, setErrors] = useState<Partial<OrderPartnerForm>>({});
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  
  useEffect(() => {
    getDeliveryPartners();
  }, []);
  
  /*Submit Form*/
  const getDeliveryPartners = async () => {
    try {
        const response: any = await axiosInstance({
          url: "deliver-partner/form-list",
          method: "GET",
        });
        //console.log('create/update response', response.data)
        //on success close the modal
        if (!response.error) {
		  setPartners(response.data.partners);
        }
    } catch (error: any) {
        console.error('Error in api request:', error);
    } finally {
        setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (order_id) {
	  console.log('order_id', order_id);	
	  console.log('delivery_partner_id', delivery_partner_id);	
	  console.log('tracking_number', tracking_number);	
      setFormData((prev) => ({
        ...prev,
        order_id: order_id ?? "",
        delivery_partner_id: delivery_partner_id ?? "",
        tracking_number: tracking_number ?? "",
      }));
    }
  }, [order_id]);

  /*Handle Form Element Value Changed*/
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsFormChanged(true);
  };

  /*Validate Form*/
  const validate = (): boolean => {
    const newErrors: Partial<OrderPartnerForm> = {};
    if (!formData.delivery_partner_id) {
      newErrors.delivery_partner_id = "Partner is required";
    }
    if (!formData.tracking_number) {
      newErrors.tracking_number = "Tracking number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /*Submit Form*/
  const handleSubmit = async () => {
    if (validate()) {
      setIsSubmitting(true);
      try {
        const response: any = await axiosInstance({
          url: "orders/update-delivery-partner",
          method: "POST",
          data: formData,
        });
        //console.log('create/update response', response.data)
        //on success close the modal
        if (!response.error) {
		  getListData();	
          handleClose();
        }
      } catch (error: any) {
        //console.error('Error in api request:', error);
        if (error?.response?.data?.type === "validation_error") {
          let errors = error.response.data.message.errors; // API returned an error message
          setErrors(errors);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    const newErrors: Partial<OrderPartnerForm> = {};
    setErrors(newErrors);
    setFormData({ order_id: "", delivery_partner_id: "", tracking_number: "" });
    handleModalOpenClose(false);
  };

  return (
    <>
	  <div
        className="modal fade show"
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered mw-650px">
          <div className="modal-content position-relative">
            {/* Close button */}
            <button
              type="button"
              className="btn btn-icon btn-active-color-primary position-absolute"
              style={{ top: "10px", right: "10px", zIndex: 10 }}
              onClick={handleClose}
            >
              ✖
            </button>
			  <div className="modal-header">
				<h2>Update Delivery Partner</h2>
			  </div>
			  <div className="modal-body mx-xl-5">
				<form className="form">
				  <select
					className={`form-select mb-2 ${errors.delivery_partner_id ? "is-invalid" : ""}`}
                    name="delivery_partner_id"
                    value={formData.delivery_partner_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Select an Partner</option>
					{partners.length && partners.map((partner) => (
					   <option key="partner._id" value={partner._id}>{partner.name}</option>
					  )
					)}
                  </select>
				  {errors.delivery_partner_id && <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">{errors.delivery_partner_id}</div>}
				  <label className="required fs-6 fw-semibold form-label mb-1">
					Tracking Number
				  </label>
				  <CommonInput
					type="text"
					name="tracking_number"
					placeholder="Enter Tracking Number"
					value={formData.tracking_number}
					onChange={handleInputChange}
					error={errors.tracking_number}
					limit={50}
				  />
				  
				  <div className="border-top mb-2" />
				  <div className="text-center">
					<button
					  type="button"
					  className="btn btn-light me-3"
					  onClick={handleClose}
					>
					  Cancel
					</button>
					{isSubmitting ? (
					  <button
						type="button"
						id="kt_modal_new_card_submit"
						className="btn btn-primary"
						disabled={true}
					  >
						<span
						  className="indicator-progress"
						  style={{ display: "block" }}
						>
						  Please wait...
						  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					  </button>
					) : (
					  <button
						type="button"
						id="kt_modal_new_card_submit"
						className="btn btn-primary"
						onClick={handleSubmit}
					  >
						<span className="indicator-label">Submit</span>
					  </button>
					)}
				  </div>
				</form>
			  </div>
			</div>
         </div>
      </div>
	  <div className="modal-backdrop fade show" />
    </>
  );
};

export default UpdateDeliveryPartner;
